import { useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/toaster";
import * as api from "@/services/api";

const AVAILABLE_ACTIONS = [
	"view",
	"create",
	"update",
	"delete",
	"export",
	"approve",
	"manage",
] as const;

const MODULES = [
	"core",
	"organization",
	"hris",
	"ams",
	"monitoring",
	"kpi",
	"admin",
	"subscription",
	"notifications",
	"gamification",
	"appraisal",
] as const;

const SCOPE_TYPES = [
	{ value: "collective", label: "Collective", description: "Works with org or department scope" },
	{ value: "personal", label: "Personal", description: "Can work with self scope" },
	{ value: "administrative", label: "Administrative", description: "Requires organization-level access" },
] as const;

interface CreateFeatureDialogProps {
	existingModules?: string[];
}

export function CreateFeatureDialog({ existingModules }: CreateFeatureDialogProps) {
	const [open, setOpen] = useState(false);
	const [code, setCode] = useState("");
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [module, setModule] = useState("");
	const [selectedActions, setSelectedActions] = useState<string[]>(["view"]);
	const [scopeType, setScopeType] = useState<string>("");

	const queryClient = useQueryClient();

	const createMutation = useMutation({
		mutationFn: api.createFeature,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["features"] });
			queryClient.invalidateQueries({ queryKey: ["dependencies"] });
			queryClient.invalidateQueries({ queryKey: ["plan-matrix"] });
			toast({
				title: "Feature created",
				description: `Feature "${name}" has been created successfully.`,
				variant: "success",
			});
			resetForm();
			setOpen(false);
		},
		onError: (error: Error) => {
			toast({
				title: "Failed to create feature",
				description: error.message,
				variant: "destructive",
			});
		},
	});

	const resetForm = () => {
		setCode("");
		setName("");
		setDescription("");
		setModule("");
		setSelectedActions(["view"]);
		setScopeType("");
	};

	const handleOpenChange = (newOpen: boolean) => {
		setOpen(newOpen);
		if (!newOpen) {
			resetForm();
		}
	};

	const handleNameChange = (value: string) => {
		setName(value);
		if (!code || code === generateCode(name)) {
			setCode(generateCode(value));
		}
	};

	const generateCode = (value: string) => {
		return value
			.toLowerCase()
			.replace(/[^a-z0-9\s_]/g, "")
			.replace(/\s+/g, "_")
			.replace(/_+/g, "_")
			.trim();
	};

	const toggleAction = (action: string) => {
		setSelectedActions((prev) =>
			prev.includes(action)
				? prev.filter((a) => a !== action)
				: [...prev, action],
		);
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (!code || !name || !module || selectedActions.length === 0) {
			toast({
				title: "Missing required fields",
				description: "Please fill in code, name, module, and select at least one action.",
				variant: "destructive",
			});
			return;
		}

		createMutation.mutate({
			code,
			name,
			description: description || undefined,
			module,
			actions: selectedActions,
			scopeType: scopeType
				? (scopeType as "collective" | "personal" | "administrative")
				: undefined,
		});
	};

	const allModules = Array.from(
		new Set([...MODULES, ...(existingModules || [])]),
	).sort();

	const isValid = code && name && module && selectedActions.length > 0;

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button size="sm">
					<Plus className="h-4 w-4 mr-2" />
					Create Feature
				</Button>
			</DialogTrigger>
			<DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Create New Feature</DialogTitle>
					<DialogDescription>
						Add a new feature (capability) to the permission registry.
					</DialogDescription>
				</DialogHeader>

				<form onSubmit={handleSubmit} className="space-y-4 mt-2">
					{/* Name */}
					<div className="space-y-2">
						<Label htmlFor="feature-name">
							Name <span className="text-red-500">*</span>
						</Label>
						<Input
							id="feature-name"
							placeholder="e.g. Employee Leave Management"
							value={name}
							onChange={(e) => handleNameChange(e.target.value)}
						/>
					</div>

					{/* Code */}
					<div className="space-y-2">
						<Label htmlFor="feature-code">
							Code <span className="text-red-500">*</span>
						</Label>
						<Input
							id="feature-code"
							placeholder="e.g. employee_leave_management"
							value={code}
							onChange={(e) => setCode(e.target.value)}
							className="font-mono text-sm"
						/>
						<p className="text-xs text-muted-foreground">
							Unique identifier. Auto-generated from name.
						</p>
					</div>

					{/* Description */}
					<div className="space-y-2">
						<Label htmlFor="feature-description">Description</Label>
						<Input
							id="feature-description"
							placeholder="Brief description of this feature"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
						/>
					</div>

					{/* Module */}
					<div className="space-y-2">
						<Label>
							Module <span className="text-red-500">*</span>
						</Label>
						<Select value={module} onValueChange={setModule}>
							<SelectTrigger>
								<SelectValue placeholder="Select a module" />
							</SelectTrigger>
							<SelectContent>
								{allModules.map((mod) => (
									<SelectItem key={mod} value={mod}>
										{mod}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{/* Actions */}
					<div className="space-y-2">
						<Label>
							Actions <span className="text-red-500">*</span>
						</Label>
						<div className="flex flex-wrap gap-3">
							{AVAILABLE_ACTIONS.map((action) => (
								<div key={action} className="flex items-center gap-2">
									<Checkbox
										id={`action-${action}`}
										checked={selectedActions.includes(action)}
										onCheckedChange={() => toggleAction(action)}
									/>
									<Label
										htmlFor={`action-${action}`}
										className="text-sm font-normal cursor-pointer"
									>
										{action}
									</Label>
								</div>
							))}
						</div>
						{selectedActions.length > 0 && (
							<div className="flex flex-wrap gap-1 mt-1">
								{selectedActions.map((action) => (
									<Badge key={action} variant="secondary" className="text-xs">
										{action}
									</Badge>
								))}
							</div>
						)}
					</div>

					{/* Scope Type */}
					<div className="space-y-2">
						<Label>Scope Type</Label>
						<Select value={scopeType} onValueChange={setScopeType}>
							<SelectTrigger>
								<SelectValue placeholder="Select scope type (optional)" />
							</SelectTrigger>
							<SelectContent>
								{SCOPE_TYPES.map((st) => (
									<SelectItem key={st.value} value={st.value}>
										<div>
											<span>{st.label}</span>
											<span className="text-xs text-muted-foreground ml-2">
												- {st.description}
											</span>
										</div>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<DialogFooter className="pt-4">
						<Button
							type="button"
							variant="outline"
							onClick={() => setOpen(false)}
							disabled={createMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							disabled={!isValid || createMutation.isPending}
						>
							{createMutation.isPending ? (
								<>
									<Loader2 className="h-4 w-4 mr-2 animate-spin" />
									Creating...
								</>
							) : (
								<>
									<Plus className="h-4 w-4 mr-2" />
									Create Feature
								</>
							)}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
